import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, UsersThree, HardDrives, Plus, UserCircle, Package,
  DownloadSimple, UploadSimple, FileXls,
} from '@phosphor-icons/react';
import { SkeletonBlock, EmptyState } from '../../../components/module-ui';
import {
  getAssetTeam, listProductClients, listAssets, getUserAssets, createAsset,
  updateAsset, deleteAsset,
  downloadClientInfraTemplate, exportClientInfra, importClientInfraFile,
} from '../api';
import { ModalShell, Field, ROLE_BADGE, fmtLabel } from '../ui';

const APP_TYPES = ['Web Application', 'Desktop', 'Mobile', 'ERP', 'API', 'Other'];
const DB_TYPES = ['PostgreSQL', 'SQL Server', 'MySQL', 'Oracle', 'MongoDB', 'Other'];
const ENVIRONMENTS = ['Production', 'UAT', 'QA', 'Development', 'DR'];
const HOSTING = [
  'Our In-House', 'AWS Cloud', 'Client Office', 'Azure Cloud',
  'Other Cloud', 'Third Party Data Center',
];
const MAINTAINED_BY = [
  'Our IT Team', 'Client IT', 'Vendor', 'DBA Team', 'Cloud Team', 'Application Team',
];

const EMPTY_INFRA = {
  application_name: '',
  application_type: 'Web Application',
  application_link: '',
  database_type: 'PostgreSQL',
  database_name: '',
  database_server_host: '',
  db_port: '',
  environment: 'Production',
  hosting_location: 'Our In-House',
  server_name: '',
  server_ip: '',
  os_version: '',
  database_maintained_by: 'Our IT Team',
  application_maintained_by: 'Our IT Team',
  infrastructure_maintained_by: 'Our IT Team',
  backup_maintained_by: 'Our IT Team',
  ssl_domain_details: '',
  monitoring: '',
  support_contact: '',
  remarks: '',
  cost: '',
  monthly_cost: '',
  status: 'available',
  warranty_expiry: '',
};

function infraToForm(asset) {
  const i = asset.infra || {};
  return {
    ...EMPTY_INFRA,
    ...i,
    application_name: i.application_name || asset.name || '',
    cost: asset.cost ?? '',
    monthly_cost: asset.monthly_cost ?? '',
    status: asset.status || 'available',
    warranty_expiry: asset.warranty_expiry ? String(asset.warranty_expiry).slice(0, 10) : '',
  };
}

function formToPayload(form, clientId, productId) {
  const infra = {
    application_name: (form.application_name || '').trim(),
    application_type: form.application_type || null,
    application_link: (form.application_link || '').trim() || null,
    database_type: form.database_type || null,
    database_name: (form.database_name || '').trim() || null,
    database_server_host: (form.database_server_host || '').trim() || null,
    db_port: (form.db_port || '').trim() || null,
    environment: form.environment || null,
    hosting_location: form.hosting_location || null,
    server_name: (form.server_name || '').trim() || null,
    server_ip: (form.server_ip || '').trim() || null,
    os_version: (form.os_version || '').trim() || null,
    database_maintained_by: form.database_maintained_by || null,
    application_maintained_by: form.application_maintained_by || null,
    infrastructure_maintained_by: form.infrastructure_maintained_by || null,
    backup_maintained_by: form.backup_maintained_by || null,
    ssl_domain_details: (form.ssl_domain_details || '').trim() || null,
    monitoring: (form.monitoring || '').trim() || null,
    support_contact: (form.support_contact || '').trim() || null,
    remarks: (form.remarks || '').trim() || null,
  };
  const typeKey = (form.application_type || 'application').toLowerCase().replace(/\s+/g, '_');
  const specsParts = [
    infra.environment,
    infra.hosting_location,
    infra.database_type,
    infra.server_name,
    infra.server_ip,
  ].filter(Boolean);
  const payload = {
    name: infra.application_name,
    asset_type: typeKey.includes('server') ? 'server' : (typeKey.includes('host') ? 'hosting' : 'domain'),
    status: form.status || 'available',
    specs: specsParts.join(' · ') || null,
    cost: form.cost === '' || form.cost == null ? null : Number(form.cost),
    monthly_cost: form.monthly_cost === '' || form.monthly_cost == null ? null : Number(form.monthly_cost),
    warranty_expiry: form.warranty_expiry || null,
    notes: infra.remarks,
    infra,
    client_id: clientId,
    product_id: productId || null,
  };
  if (!payload.warranty_expiry) delete payload.warranty_expiry;
  return payload;
}

function ClientDetailPage() {
  const { teamId, clientId } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('members');

  const [infraAssets, setInfraAssets] = useState([]);
  const [infraLoading, setInfraLoading] = useState(false);
  const [infraModal, setInfraModal] = useState(null);
  const [infraForm, setInfraForm] = useState(EMPTY_INFRA);
  const [infraSaving, setInfraSaving] = useState(false);
  const [infraError, setInfraError] = useState(null);
  const [showInfraImport, setShowInfraImport] = useState(false);
  const [infraImportFile, setInfraImportFile] = useState(null);
  const [infraImporting, setInfraImporting] = useState(false);
  const [infraImportResult, setInfraImportResult] = useState(null);
  const [infraFileBusy, setInfraFileBusy] = useState(null);
  const [memberAssetCounts, setMemberAssetCounts] = useState({});

  const refreshClient = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const t = await getAssetTeam(teamId);
      setTeam(t);
      if (!t.product_id) {
        setError('This team has no associated product');
        return;
      }
      const rows = await listProductClients(t.product_id);
      const found = rows.find((c) => c.client_id === clientId);
      if (!found) {
        setError('Client not found');
      } else {
        setClient(found);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load client');
    } finally {
      setLoading(false);
    }
  }, [teamId, clientId]);

  useEffect(() => {
    setActiveTab('members');
    refreshClient();
  }, [refreshClient]);

  const refreshInfra = useCallback(() => {
    setInfraLoading(true);
    listAssets({ client_id: clientId })
      .then(setInfraAssets)
      .catch((e) => console.error('Failed to fetch infrastructure:', e))
      .finally(() => setInfraLoading(false));
  }, [clientId]);

  useEffect(() => {
    if (activeTab === 'infrastructure') refreshInfra();
  }, [activeTab, refreshInfra]);

  const members = useMemo(
    () => (team ? team.members.filter((m) => (m.client_ids || []).includes(clientId)) : []),
    [team, clientId],
  );

  useEffect(() => {
    if (members.length === 0) return undefined;
    let cancelled = false;
    Promise.all(members.map((m) =>
      getUserAssets(m.user_id)
        .then((assets) => [m.user_id, assets.filter((a) => a.client_id === clientId || a.assigned_client_id === clientId).length])
        .catch(() => [m.user_id, 0]),
    )).then((entries) => {
      if (!cancelled) setMemberAssetCounts(Object.fromEntries(entries));
    });
    return () => { cancelled = true; };
  }, [members, clientId]);

  const openInfraModal = (asset) => {
    setInfraError(null);
    setInfraModal(asset || 'create');
    setInfraForm(asset ? infraToForm(asset) : { ...EMPTY_INFRA });
  };

  const saveInfraAsset = async (e) => {
    e.preventDefault();
    if (!(infraForm.application_name || '').trim()) {
      setInfraError('Application Name is required');
      return;
    }
    setInfraSaving(true);
    setInfraError(null);
    try {
      const payload = formToPayload(infraForm, clientId, team?.product_id);
      if (infraModal === 'create') {
        await createAsset(payload);
      } else {
        await updateAsset(infraModal.asset_id, payload);
      }
      setInfraModal(null);
      refreshInfra();
    } catch (err) {
      setInfraError(err.response?.data?.detail || 'Failed to save infrastructure');
    } finally {
      setInfraSaving(false);
    }
  };

  const removeInfraAsset = async (asset) => {
    if (!window.confirm(`Delete "${asset.name}"?`)) return;
    try {
      await deleteAsset(asset.asset_id);
      refreshInfra();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete asset');
    }
  };

  const safeClientFile = (suffix) => {
    const base = (client?.name || 'client').replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 40) || 'client';
    return `${base}_${suffix}`;
  };

  const handleDownloadInfraTemplate = async () => {
    setInfraFileBusy('template');
    try {
      await downloadClientInfraTemplate(clientId, safeClientFile('infrastructure_template.xlsx'));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to download template');
    } finally {
      setInfraFileBusy(null);
    }
  };

  const handleExportInfra = async () => {
    setInfraFileBusy('export');
    try {
      await exportClientInfra(clientId, safeClientFile('infrastructure_export.xlsx'));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to export infrastructure');
    } finally {
      setInfraFileBusy(null);
    }
  };

  const handleInfraImport = async () => {
    if (!infraImportFile) return;
    setInfraImporting(true);
    setInfraImportResult(null);
    try {
      const result = await importClientInfraFile(clientId, infraImportFile);
      setInfraImportResult(result);
      refreshInfra();
    } catch (err) {
      setInfraImportResult({ error: err.response?.data?.detail || 'Import failed' });
    } finally {
      setInfraImporting(false);
    }
  };

  if (loading) return <SkeletonBlock />;
  if (error || !client) {
    return <EmptyState icon={UsersThree} title="Client unavailable" description={error || 'Client not found'} />;
  }

  return (
    <div data-testid="client-detail">
      <Link to={`/dashboard/assets/teams/${teamId}`} style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px',
        color: '#002FA7', textDecoration: 'none', marginBottom: '8px', fontWeight: '600',
      }}>
        <ArrowLeft size={14} /> {team.name}
      </Link>

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '14px', marginBottom: '8px' }}>
        <p style={{ fontSize: '14px', fontWeight: '700', color: '#111827', margin: 0 }}>
          {client.name} {!client.active && <span style={{ fontSize: '11px', color: '#9CA3AF' }}>inactive</span>}
        </p>
        <p style={{ fontSize: '12px', color: '#6B7280', margin: '4px 0 0' }}>
          {members.length} member{members.length === 1 ? '' : 's'}
        </p>
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '8px', borderBottom: '1px solid #E5E7EB' }}>
        {[
          { id: 'members', label: 'Team Members', icon: UsersThree },
          { id: 'infrastructure', label: 'Infrastructure', icon: HardDrives },
        ].map((t) => (
          <button key={t.id} type="button" onClick={() => setActiveTab(t.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
              background: 'none', border: 'none', borderBottom: activeTab === t.id ? '2px solid #002FA7' : '2px solid transparent',
              color: activeTab === t.id ? '#002FA7' : '#6B7280', fontWeight: activeTab === t.id ? '600' : '500',
              fontSize: '12px', cursor: 'pointer',
            }}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'members' && (
        members.length === 0 ? (
          <EmptyState icon={UsersThree} title="No team members" description="Assign members to this client from Product Management or the team Clients tab." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {members.map((m) => (
                <div key={m.user_id}
                  onClick={() => navigate(`/dashboard/assets/teams/${teamId}/members/${m.user_id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                    padding: '10px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', background: 'white',
                  }}>
                  {m.picture ? (
                    <img src={m.picture} alt="" style={{ width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <UserCircle size={30} color="#9CA3AF" />
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={{ fontSize: '12px', fontWeight: '600', margin: 0 }}>{m.name}</p>
                    <p style={{ fontSize: '11px', color: '#6B7280', margin: '2px 0 0' }}>
                      {m.designation || m.email}
                    </p>
                  </div>
                  <span className="badge" style={{ background: ROLE_BADGE[m.org_role] || '#F3F4F6', fontSize: '11px', textTransform: 'capitalize' }}>
                    {m.org_role}
                  </span>
                  <span className="badge" style={{
                    background: memberAssetCounts[m.user_id] > 0 ? '#DBEAFE' : '#F3F4F6',
                    fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
                  }}>
                    <Package size={12} /> {memberAssetCounts[m.user_id] ?? 0}
                  </span>
                </div>
            ))}
          </div>
        )
      )}

      {activeTab === 'infrastructure' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <p style={{ fontSize: '12px', color: '#6B7280', margin: 0, maxWidth: '420px' }}>
              Download the template, fill one row per application/server, then import. Export downloads the current inventory for this client.
            </p>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleDownloadInfraTemplate}
                className="btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                disabled={!!infraFileBusy}
                data-testid="infra-template-button"
              >
                <FileXls size={15} /> {infraFileBusy === 'template' ? '…' : 'Template'}
              </button>
              <button
                type="button"
                onClick={handleExportInfra}
                className="btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                disabled={!!infraFileBusy}
                data-testid="infra-export-button"
              >
                <DownloadSimple size={15} /> {infraFileBusy === 'export' ? '…' : 'Export'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setInfraImportFile(null);
                  setInfraImportResult(null);
                  setShowInfraImport(true);
                }}
                className="btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                data-testid="infra-import-button"
              >
                <UploadSimple size={15} /> Import
              </button>
              <button onClick={() => openInfraModal(null)} className="btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }} data-testid="create-infra-button">
                <Plus size={16} weight="bold" /> Add Infrastructure
              </button>
            </div>
          </div>
          {infraLoading ? (
            <SkeletonBlock />
          ) : infraAssets.length === 0 ? (
            <EmptyState
              icon={HardDrives}
              title="No infrastructure tracked"
              description="Download the template for this client, fill it in Excel, then Import — or add a row manually."
            />
          ) : (
            <div className="table-container" style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Application</th>
                    <th>Type</th>
                    <th>Env</th>
                    <th>Hosting</th>
                    <th>DB</th>
                    <th>Server</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {infraAssets.map((a) => {
                    const i = a.infra || {};
                    return (
                      <tr key={a.asset_id}>
                        <td style={{ fontWeight: '600' }}>
                          {i.application_name || a.name}
                          {i.application_link ? (
                            <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: 400 }}>{i.application_link}</div>
                          ) : null}
                        </td>
                        <td>{i.application_type || fmtLabel(a.asset_type)}</td>
                        <td>{i.environment || '—'}</td>
                        <td>{i.hosting_location || '—'}</td>
                        <td>{i.database_type || a.specs || '—'}</td>
                        <td>
                          {i.server_name || '—'}
                          {i.server_ip ? <div style={{ fontSize: '11px', color: '#6B7280' }}>{i.server_ip}</div> : null}
                        </td>
                        <td>
                          <button onClick={() => openInfraModal(a)} className="btn-secondary" style={{ padding: '4px 10px', fontSize: '12px', marginRight: '6px' }}>Edit</button>
                          <button onClick={() => removeInfraAsset(a)} className="btn-secondary" style={{ padding: '4px 10px', fontSize: '12px', color: '#B91C1C' }}>Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showInfraImport && (
        <ModalShell title="Import Infrastructure" onClose={() => setShowInfraImport(false)} testid="infra-import-modal">
          <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 10px' }}>
            Use the client template (.xlsx). Keep the header row. One row = one application / environment.
            Re-importing the same Application Name (or Asset ID from an export) updates the existing row.
          </p>
          <button
            type="button"
            onClick={handleDownloadInfraTemplate}
            className="btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}
            disabled={!!infraFileBusy}
          >
            <FileXls size={15} /> Download blank template
          </button>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => {
              setInfraImportFile(e.target.files?.[0] || null);
              setInfraImportResult(null);
            }}
            style={{ display: 'block', marginBottom: '8px', width: '100%' }}
            data-testid="infra-import-file-input"
          />
          {infraImportResult && (
            <div style={{
              padding: '10px 12px', borderRadius: '8px', marginBottom: '10px',
              background: infraImportResult.error ? '#FEE2E2' : '#ECFDF5', fontSize: '12px',
            }}>
              {infraImportResult.error ? (
                <p style={{ color: '#991B1B', margin: 0 }}>{String(infraImportResult.error)}</p>
              ) : (
                <>
                  <p style={{ margin: 0, color: '#065F46' }}>
                    Import complete — {infraImportResult.created} created, {infraImportResult.updated} updated,
                    {' '}{infraImportResult.skipped} skipped (of {infraImportResult.total_rows} rows).
                  </p>
                  {infraImportResult.errors?.length > 0 && (
                    <ul style={{ margin: '6px 0 0', paddingLeft: '18px', color: '#92400E' }}>
                      {infraImportResult.errors.map((errMsg, i) => <li key={i}>{errMsg}</li>)}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleInfraImport}
              className="btn-primary"
              style={{ flex: 1, opacity: !infraImportFile || infraImporting ? 0.6 : 1 }}
              disabled={!infraImportFile || infraImporting}
              data-testid="submit-infra-import-button"
            >
              {infraImporting ? 'Importing…' : 'Import'}
            </button>
            <button type="button" onClick={() => setShowInfraImport(false)} className="btn-secondary" style={{ flex: 1 }}>
              Close
            </button>
          </div>
        </ModalShell>
      )}

      {infraModal && (
        <ModalShell
          title={infraModal === 'create' ? 'Add Infrastructure' : 'Edit Infrastructure'}
          onClose={() => setInfraModal(null)}
          testid="infra-modal"
        >
          <form onSubmit={saveInfraAsset}>
            <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 10px' }}>
              Fields match the Client Application Database Server Inventory sheet.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', maxHeight: '60vh', overflowY: 'auto', paddingRight: '4px' }}>
              <Field label="Application Name *">
                <input type="text" required className="input-field" value={infraForm.application_name}
                  onChange={(e) => setInfraForm({ ...infraForm, application_name: e.target.value })} />
              </Field>
              <Field label="Application Type">
                <select className="input-field" value={infraForm.application_type}
                  onChange={(e) => setInfraForm({ ...infraForm, application_type: e.target.value })}>
                  {APP_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </Field>
              <Field label="Application Link">
                <input type="text" className="input-field" value={infraForm.application_link}
                  onChange={(e) => setInfraForm({ ...infraForm, application_link: e.target.value })}
                  placeholder="app.example.com" />
              </Field>
              <Field label="Environment">
                <select className="input-field" value={infraForm.environment}
                  onChange={(e) => setInfraForm({ ...infraForm, environment: e.target.value })}>
                  {ENVIRONMENTS.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </Field>
              <Field label="Database Type">
                <select className="input-field" value={infraForm.database_type}
                  onChange={(e) => setInfraForm({ ...infraForm, database_type: e.target.value })}>
                  {DB_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </Field>
              <Field label="Database Name">
                <input type="text" className="input-field" value={infraForm.database_name}
                  onChange={(e) => setInfraForm({ ...infraForm, database_name: e.target.value })} />
              </Field>
              <Field label="Database Server / Host">
                <input type="text" className="input-field" value={infraForm.database_server_host}
                  onChange={(e) => setInfraForm({ ...infraForm, database_server_host: e.target.value })} />
              </Field>
              <Field label="DB Port">
                <input type="text" className="input-field" value={infraForm.db_port}
                  onChange={(e) => setInfraForm({ ...infraForm, db_port: e.target.value })} />
              </Field>
              <Field label="Hosting Location">
                <select className="input-field" value={infraForm.hosting_location}
                  onChange={(e) => setInfraForm({ ...infraForm, hosting_location: e.target.value })}>
                  {HOSTING.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </Field>
              <Field label="Server Name">
                <input type="text" className="input-field" value={infraForm.server_name}
                  onChange={(e) => setInfraForm({ ...infraForm, server_name: e.target.value })} />
              </Field>
              <Field label="Server IP / Public IP">
                <input type="text" className="input-field" value={infraForm.server_ip}
                  onChange={(e) => setInfraForm({ ...infraForm, server_ip: e.target.value })} />
              </Field>
              <Field label="OS / Version">
                <input type="text" className="input-field" value={infraForm.os_version}
                  onChange={(e) => setInfraForm({ ...infraForm, os_version: e.target.value })} />
              </Field>
              <Field label="Database Maintained By">
                <select className="input-field" value={infraForm.database_maintained_by}
                  onChange={(e) => setInfraForm({ ...infraForm, database_maintained_by: e.target.value })}>
                  {MAINTAINED_BY.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </Field>
              <Field label="Application Maintained By">
                <select className="input-field" value={infraForm.application_maintained_by}
                  onChange={(e) => setInfraForm({ ...infraForm, application_maintained_by: e.target.value })}>
                  {MAINTAINED_BY.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </Field>
              <Field label="Infrastructure Maintained By">
                <select className="input-field" value={infraForm.infrastructure_maintained_by}
                  onChange={(e) => setInfraForm({ ...infraForm, infrastructure_maintained_by: e.target.value })}>
                  {MAINTAINED_BY.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </Field>
              <Field label="Backup Maintained By">
                <select className="input-field" value={infraForm.backup_maintained_by}
                  onChange={(e) => setInfraForm({ ...infraForm, backup_maintained_by: e.target.value })}>
                  {MAINTAINED_BY.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </Field>
              <Field label="SSL / Domain Details">
                <input type="text" className="input-field" value={infraForm.ssl_domain_details}
                  onChange={(e) => setInfraForm({ ...infraForm, ssl_domain_details: e.target.value })} />
              </Field>
              <Field label="Monitoring">
                <input type="text" className="input-field" value={infraForm.monitoring}
                  onChange={(e) => setInfraForm({ ...infraForm, monitoring: e.target.value })}
                  placeholder="Yes / No / tool name" />
              </Field>
              <Field label="Support Contact">
                <input type="text" className="input-field" value={infraForm.support_contact}
                  onChange={(e) => setInfraForm({ ...infraForm, support_contact: e.target.value })} />
              </Field>
              <Field label="Purchase / setup cost">
                <input type="number" step="0.01" min="0" className="input-field" value={infraForm.cost}
                  onChange={(e) => setInfraForm({ ...infraForm, cost: e.target.value })} />
              </Field>
              <Field label="Monthly cost (hosting etc.)">
                <input type="number" step="0.01" min="0" className="input-field" value={infraForm.monthly_cost}
                  onChange={(e) => setInfraForm({ ...infraForm, monthly_cost: e.target.value })} />
              </Field>
              <Field label="Renewal / warranty">
                <input type="date" className="input-field" value={infraForm.warranty_expiry}
                  onChange={(e) => setInfraForm({ ...infraForm, warranty_expiry: e.target.value })} />
              </Field>
              <Field label="Status">
                <select className="input-field" value={infraForm.status}
                  onChange={(e) => setInfraForm({ ...infraForm, status: e.target.value })}>
                  <option value="available">Available</option>
                  <option value="assigned">Assigned</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="retired">Retired</option>
                </select>
              </Field>
            </div>
            <Field label="Remarks">
              <textarea className="input-field" rows={2} value={infraForm.remarks}
                onChange={(e) => setInfraForm({ ...infraForm, remarks: e.target.value })} />
            </Field>
            {infraError && <p style={{ color: '#B91C1C', fontSize: '12px', marginBottom: '8px' }}>{infraError}</p>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button type="submit" className="btn-primary" style={{ flex: 1 }} disabled={infraSaving}>{infraSaving ? 'Saving…' : 'Save'}</button>
              <button type="button" onClick={() => setInfraModal(null)} className="btn-secondary" style={{ flex: 1 }}>Cancel</button>
            </div>
          </form>
        </ModalShell>
      )}
    </div>
  );
}

export default ClientDetailPage;
